import { GoogleGenerativeAI } from '@google/generative-ai';
import { sheetsService } from './googleSheetsService';

// Initialize Gemini
const genAI = new GoogleGenerativeAI(process.env.REACT_APP_GEMINI_API_KEY || '');

interface SongInfo {
  title: string;
  song: string;
  artist: string;
}

// Define VideoSearchResult interface
export interface VideoSearchResult {
  id: string;
  title: string;
  thumbnailUrl: string;
  viewCount: string;
}

export const extractSongInfo = async (videoTitle: string): Promise<{ song: string; artist: string }> => {
  try {
    const model = genAI.getGenerativeModel({ model: 'gemini-1.5-pro' });
    
    const prompt = `Extract the song name and artist from this YouTube video title. Return only the song name and artist in JSON format like this: {"song": "song name", "artist": "artist name"}. If you can't determine either, use "Unknown". Title: ${videoTitle}`;
    
    const result = await model.generateContent(prompt);
    const geminiResponse = await result.response;
    const text = geminiResponse.text();
    
    // Parse the JSON response
    const parsedResponse = JSON.parse(text);
    return {
      song: parsedResponse.song || 'Unknown',
      artist: parsedResponse.artist || 'Unknown'
    };
  } catch (error) {
    console.error('Error extracting song info:', error);
    return { song: 'Unknown', artist: 'Unknown' };
  }
};

export const youtubeService = {
  async getSongInfo(youtubeUrl: string): Promise<SongInfo> {
    try {
      console.log('Starting getSongInfo...');
      
      // Ensure Google APIs are initialized
      console.log('Checking if signed in...');
      if (!sheetsService.isSignedIn()) {
        console.log('Not signed in, initializing...');
        await sheetsService.init();
        await sheetsService.signIn();
      }
      console.log('Signed in and initialized');

      // Extract video ID from URL
      console.log('Extracting video ID from URL:', youtubeUrl);
      const videoId = youtubeUrl.match(/(?:youtube\.com\/(?:[^\/]+\/.+\/|(?:v|e(?:mbed)?)\/|.*[?&]v=)|youtu\.be\/)([^"&?\/\s]{11})/)?.[1];
      
      if (!videoId) {
        throw new Error('Invalid YouTube URL');
      }
      console.log('Video ID extracted:', videoId);

      // Get video title from YouTube API using fetch
      console.log('Making YouTube API request...');
      const response = await fetch(`https://www.googleapis.com/youtube/v3/videos?part=snippet&id=${videoId}&key=${process.env.REACT_APP_GOOGLE_API_KEY}`);
      
      if (!response.ok) {
        throw new Error(`YouTube API error: ${response.statusText}`);
      }

      const videoData = await response.json();
      console.log('YouTube API response:', videoData);
      
      if (!videoData.items?.[0]) {
        throw new Error('Video not found');
      }

      const title = videoData.items[0].snippet?.title || '';
      console.log('Video title:', title);
      
      // Use Gemini to parse the title
      console.log('Using Gemini to parse title...');
      const model = genAI.getGenerativeModel({ model: "gemini-1.5-pro" });
      
      const prompt = `You are a music metadata parser. Extract the song name and artist from the given YouTube video title. Return the response in JSON format with 'song' and 'artist' fields. If you can't determine the artist, set it to 'Unknown Artist'. Sometimes the featured artist might be included after the song name (ex: Morgan Wallen - I Ain't Comin' Back (feat. Post Malone)). In that case, combine the featured artist with the main artist so in this case it would be "Morgan Wallen feat. Post Malone".

Title: ${title}`;

      const result = await model.generateContent(prompt);
      const geminiResponse = await result.response;
      const text = geminiResponse.text();
      console.log('Gemini response:', text);
      
      try {
        // Extract JSON from markdown code block if present
        const jsonMatch = text.match(/```(?:json)?\s*(\{[\s\S]*?\})\s*```/);
        const jsonText = jsonMatch ? jsonMatch[1] : text;
        
        const parsedResult = JSON.parse(jsonText);
        return {
          title,
          song: parsedResult.song,
          artist: parsedResult.artist
        };
      } catch (error) {
        console.error('Failed to parse Gemini response:', error);
        throw new Error('Failed to parse song information');
      }
      
    } catch (error) {
      console.error('Error in getSongInfo:', error);
      throw error;
    }
  },

  // Add searchVideos function
  async searchVideos(query: string): Promise<VideoSearchResult[]> {
    try {
      const apiKey = process.env.REACT_APP_GOOGLE_API_KEY;
      if (!apiKey) {
        throw new Error('Missing Google API Key');
      }

      console.log('Searching YouTube for:', query);
      const searchResponse = await fetch(
        `https://www.googleapis.com/youtube/v3/search?part=snippet&q=${encodeURIComponent(query)}&type=video&maxResults=10&key=${apiKey}`
      );

      if (!searchResponse.ok) {
        const errorData = await searchResponse.json();
        console.error('YouTube Search API error:', errorData);
        throw new Error(`YouTube Search API error: ${searchResponse.statusText}`);
      }

      const searchData = await searchResponse.json();
      console.log('YouTube Search API response:', searchData);

      if (!searchData.items || searchData.items.length === 0) {
        return [];
      }

      // Get video IDs
      const videoIds = searchData.items.map((item: any) => item.id.videoId).join(',');

      // Fetch video statistics (including view count)
      const statsResponse = await fetch(
        `https://www.googleapis.com/youtube/v3/videos?part=snippet,statistics&id=${videoIds}&key=${apiKey}`
      );

      if (!statsResponse.ok) {
        const errorData = await statsResponse.json();
        console.error('YouTube Videos API error:', errorData);
        throw new Error(`YouTube Videos API error: ${statsResponse.statusText}`);
      }

      const statsData = await statsResponse.json();
      console.log('YouTube Videos API response:', statsData);

      if (!statsData.items) {
        return [];
      }

      // Map results and sort by view count (descending)
      const results = statsData.items
        .map((item: any) => ({
          id: item.id,
          title: item.snippet.title,
          thumbnailUrl: item.snippet.thumbnails.default.url,
          viewCount: item.statistics?.viewCount || '0',
        }))
        .sort((a: VideoSearchResult, b: VideoSearchResult) => parseInt(b.viewCount) - parseInt(a.viewCount));

      console.log('Formatted & Sorted results:', results);
      return results;

    } catch (error) {
      console.error('Error in searchVideos:', error);
      throw error;
    }
  },
}; 